import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { endpointMessageReceived } from '../../../base/conference/actions';
import { getRoomName } from '../../../base/conference/functions';
import { getLocalParticipant } from '../../../base/participants/functions';
import { getDisplayName } from '../../../base/settings/functions.web';
import { isCaptioner } from '../../../base/user-role/functions';
import { CaptionAudioMixer, getLiveCaptionAudioTracks as getLiveAudioTracks } from '../../captionAudioMixer';
import { ensureLocalCaptionDisplay } from '../../captionDisplay';
import { parseCaptionRelayAuth } from '../../captionRelayAuth';
import { parseCaptionStreamEvent } from '../../captionStreamEvent';
import logger from '../../../subtitles/logger';
import { CAPTIONER_LANGUAGES, DEFAULT_CAPTION_LANGUAGE, normalizeCaptionLanguage } from '../../languages';
import { analyzeCaptionTone, normalizeCaptionToneProfile } from '../../../subtitles/captionTone';
import { getPersistentItem } from '../../../vrs-auth/storage';
const DEFAULT_TRACK = 'mixed';
const DEEPGRAM_MIME_TYPE = 'audio/webm;codecs=opus';
const MAX_RECONNECT_ATTEMPTS = 5;
const RECENT_HISTORY_LIMIT = 8;
const CAPTION_TRACKS = [
    { label: 'Mixed conversation', value: 'mixed' },
    { label: 'Hearing speaker', value: 'hearing' },
    { label: 'Deaf signer', value: 'deaf' },
    { label: 'Interpreter', value: 'interpreter' }
];
function getStoredCaptionRelayAuth() {
    return parseCaptionRelayAuth(getPersistentItem('vrs_auth_token'), getPersistentItem('vrs_user_info'));
}
function getWsUrl() {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/ws`;
}
function CaptionComposer({ _conference, _displayName, _isCaptioner, _localParticipantId, _localParticipantName, _requestingSubtitles, _roomName, _tracks, dispatch }) {
    const [captionText, setCaptionText] = useState('');
    const [publishError, setPublishError] = useState(null);
    const [language, setLanguage] = useState(DEFAULT_CAPTION_LANGUAGE);
    const [track, setTrack] = useState(DEFAULT_TRACK);
    const [sentCaptions, setSentCaptions] = useState([]);
    const [autoStatus, setAutoStatus] = useState('Auto captions idle');
    const [autoSessionId, setAutoSessionId] = useState(null);
    const [autoInputCount, setAutoInputCount] = useState(0);
    // True from Start until the stream stops for good — including while a
    // reconnect backoff is pending — so the button honestly reflects state.
    const [autoWanted, setAutoWanted] = useState(false);
    // Sticks after the reconnect ladder gives up, so the Start/Stop button
    // shows a visible failure state instead of quietly reading "Start" again.
    // Cleared only by a deliberate stop or a successfully (re)started stream.
    const [autoError, setAutoError] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const audioContextRef = useRef(null);
    const audioDestinationRef = useRef(null);
    const audioMixerRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const sessionIdRef = useRef(null);
    // Stable caption id for the in-flight Deepgram utterance (see
    // publishAutoCaption); empty once its final lands.
    const deepgramUtteranceIdRef = useRef('');
    const streamRef = useRef(null);
    const wsRef = useRef(null);
    // Retry bookkeeping: autoIntent mirrors autoWanted without re-render
    // timing hazard inside socket callbacks; closingByUser suppresses the
    // reconnect path when WE closed the socket deliberately.
    const autoIntentRef = useRef(false);
    const closingByUserRef = useRef(false);
    const reconnectAttemptRef = useRef(0);
    const reconnectTimerRef = useRef(null);
    // Socket callbacks and the reconnect timer run from whichever render's
    // closure created them; this ref carries the current language so a
    // restarted stream never resurrects an earlier language selection.
    const languageRef = useRef(language);
    languageRef.current = language;
    // This token is local to one conference/role/participant, never a wire ID.
    const composerOwner = useMemo(() => ({}), [_conference, _roomName, _isCaptioner, _localParticipantId]);
    const composerOwnerRef = useRef(composerOwner);
    const [manualOwner, setManualOwner] = useState(composerOwner);
    composerOwnerRef.current = composerOwner;
    // Reset before committing a new owner's view, including a role round trip.
    if (manualOwner !== composerOwner) {
        setManualOwner(composerOwner);
        setCaptionText('');
        setSentCaptions([]);
        setEditingEntry(null);
        setPublishError(null);
    }
    useLayoutEffect(() => {
        composerOwnerRef.current = composerOwner;
        return () => {
            if (composerOwnerRef.current === composerOwner)
                composerOwnerRef.current = null;
        };
    }, [composerOwner]);
    const participantName = useMemo(() => _localParticipantName || _displayName || 'Transcriber', [_displayName, _localParticipantName]);
    // Legacy free-text sessions could persist any string; surface it rather
    // than silently snapping an unlisted stored value back to the first option.
    const languageOptions = useMemo(() => (CAPTIONER_LANGUAGES.some(option => option.value === language)
        ? CAPTIONER_LANGUAGES
        : [...CAPTIONER_LANGUAGES, { label: `${language} (custom)`, value: language }]), [language]);
    // Release each owned resource independently. Never log audio, transcript,
    // auth material or vendor exception objects from these boundaries.
    const releaseCaptionResource = (resource, release) => {
        try {
            const pending = release();
            if (pending) {
                pending.catch(() => logger.warn(`Caption ${resource} cleanup failed.`));
            }
        }
        catch {
            logger.warn(`Caption ${resource} cleanup failed.`);
        }
    };
    const stopAutoRecorder = () => {
        const recorder = mediaRecorderRef.current;
        mediaRecorderRef.current = null;
        if (recorder) {
            recorder.ondataavailable = null;
            recorder.onerror = null;
            releaseCaptionResource('recorder', () => {
                if (recorder.state !== 'inactive')
                    recorder.stop();
            });
        }
    };
    const stopAutoCaptions = () => {
        autoIntentRef.current = false;
        setAutoWanted(false);
        setAutoError(false);
        if (reconnectTimerRef.current !== null) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }
        const sessionId = sessionIdRef.current;
        const socket = wsRef.current;
        const stream = streamRef.current;
        const mixer = audioMixerRef.current;
        const context = audioContextRef.current;
        // Relinquish ownership before invoking vendor APIs or queued callbacks.
        closingByUserRef.current = Boolean(socket);
        wsRef.current = null;
        sessionIdRef.current = null;
        streamRef.current = null;
        audioDestinationRef.current = null;
        audioMixerRef.current = null;
        setAutoInputCount(0);
        audioContextRef.current = null;
        deepgramUtteranceIdRef.current = '';
        setAutoSessionId(null);
        setAutoStatus('Auto captions idle');
        if (sessionId && socket?.readyState === WebSocket.OPEN) {
            releaseCaptionResource('stop signal', () => socket.send(JSON.stringify({
                data: { sessionId }, type: 'caption_stream_stop'
            })));
        }
        stopAutoRecorder();
        // These are the mixer's output tracks; the room's input tracks are not ours to stop.
        releaseCaptionResource('mixed audio tracks', () => {
            stream?.getTracks().forEach(mediaTrack => releaseCaptionResource('mixed audio track', () => mediaTrack.stop()));
        });
        if (mixer)
            releaseCaptionResource('audio mixer', () => mixer.dispose());
        if (context)
            releaseCaptionResource('audio context', () => context.close());
        if (socket)
            releaseCaptionResource('relay socket', () => socket.close());
    };
    const failAutoCaptions = (message) => {
        stopAutoCaptions();
        logger.error('Automatic captions stopped after a stream failure.');
        setAutoError(true);
        setAutoStatus(message);
    };
    // A mounted composer can survive conference/role changes. Its audio and
    // callbacks must never survive the room (or captioner role) that owns them.
    useLayoutEffect(() => () => stopAutoCaptions(), [_conference, _roomName, _isCaptioner, _localParticipantId]);
    // A composing captioner must see their own captions regardless of the
    // enabledByDefault preference or CC-dialog state — publishing blind is
    // how mis-captions ship.
    useEffect(() => {
        if (_isCaptioner) {
            ensureLocalCaptionDisplay(dispatch, {
                conferenceJoined: Boolean(_conference),
                requestingSubtitles: _requestingSubtitles
            });
        }
    }, [_conference, _isCaptioner, _requestingSubtitles, dispatch]);
    useEffect(() => {
        if (autoWanted) {
            try {
                audioMixerRef.current?.sync(getLiveAudioTracks(_tracks));
            }
            catch {
                failAutoCaptions('Room audio sources could not be updated.');
            }
        }
    }, [_tracks, autoWanted]);
    const publishCaption = () => {
        if (!_isCaptioner || !_conference || composerOwnerRef.current !== composerOwner)
            return;
        const text = captionText.trim();
        const normalizedLanguage = normalizeCaptionLanguage(language);
        const normalizedTrack = CAPTION_TRACKS.some(option => option.value === track) ? track : DEFAULT_TRACK;
        if (!text) {
            return;
        }
        const replaceTarget = editingEntry;
        const timestamp = Date.now();
        // Republishing under the original message_id makes every viewer's
        // overlay swap the line in place (the middleware keys transcripts by
        // message_id) instead of appending a duplicate.
        const messageId = replaceTarget
            ? replaceTarget.id
            : `captioner-${timestamp}-${Math.random().toString(36).slice(2, 8)}`;
        const payload = {
            captionTone: analyzeCaptionTone({ text }),
            is_interim: false,
            language: normalizedLanguage,
            message_id: messageId,
            participant: {
                avatar_url: '',
                id: _localParticipantId || 'captioner-local',
                name: participantName
            },
            source: 'human-captioner',
            stability: 1,
            timestamp,
            track: normalizedTrack,
            transcript: [{ text }],
            type: 'transcription-result'
        };
        try {
            _conference.sendEndpointMessage('', payload);
        }
        catch {
            setPublishError('This caption could not be published. Try again.');
            return;
        }
        setPublishError(null);
        dispatch(endpointMessageReceived({
            id: _localParticipantId || 'captioner-local',
            name: participantName
        }, payload));
        setSentCaptions(previous => {
            if (replaceTarget) {
                return previous.map(entry => (entry.id === messageId
                    ? { ...entry, language: normalizedLanguage, text, timestamp, track: normalizedTrack }
                    : entry));
            }
            return [
                { id: messageId, language: normalizedLanguage, text, timestamp, track: normalizedTrack },
                ...previous
            ].slice(0, RECENT_HISTORY_LIMIT);
        });
        setEditingEntry(null);
        setCaptionText('');
    };
    const onSubmit = (event) => {
        event.preventDefault();
        publishCaption();
    };
    const publishAutoCaption = (data) => {
        if (!_isCaptioner || !_conference || composerOwnerRef.current !== composerOwner)
            return;
        const timestamp = data.timestamp ?? Date.now();
        /*
         * One stable message_id per spoken utterance. Deepgram live events
         * arrive as interims finishing in a single final per phrase, and the
         * subtitles middleware keys caption rows by message_id — keying by
         * wall-clock timestamp stacked "Hel" / "Hello wor" / "Hello world."
         * as separate lingering lines on every viewer. The active id is
         * reused until the final lands (which then replaces its own interims
         * in place), then cleared so the next phrase starts fresh.
         */
        if (!deepgramUtteranceIdRef.current) {
            deepgramUtteranceIdRef.current
                = `deepgram-${data.sessionId}${data.isFinal ? '' : '-interim'}-${timestamp}`;
        }
        const messageId = deepgramUtteranceIdRef.current;
        // Publish interim words immediately with plain presentation. Provider
        // tone metadata is eligible only once the phrase has been finalized.
        const captionTone = !data.isFinal ? analyzeCaptionTone({ text: '' })
            : normalizeCaptionToneProfile(data.captionTone) ?? analyzeCaptionTone({
                providerConfidence: data.confidence,
                text: data.transcript
            });
        const payload = {
            captionTone,
            confidence: data.confidence,
            is_interim: !data.isFinal,
            language: data.language || normalizeCaptionLanguage(languageRef.current),
            message_id: messageId,
            participant: {
                avatar_url: '',
                id: _localParticipantId || 'captioner-auto',
                name: 'Auto captions'
            },
            source: 'deepgram-live-audio',
            stability: data.isFinal ? 1 : 0.7,
            timestamp,
            // The recorder mixes all room inputs; it cannot identify a conversational role.
            track: DEFAULT_TRACK,
            transcript: [{ text: data.transcript }],
            type: 'transcription-result'
        };
        try {
            if (!_conference)
                throw new Error('Conference unavailable');
            _conference.sendEndpointMessage('', payload);
        }
        catch {
            failAutoCaptions('Automatic captions could not be published to the room.');
            return;
        }
        dispatch(endpointMessageReceived({
            id: _localParticipantId || 'captioner-auto',
            name: 'Auto captions'
        }, payload));
        if (data.isFinal) {
            setSentCaptions(previous => [
                {
                    id: messageId,
                    language: payload.language,
                    text: data.transcript,
                    timestamp,
                    track: payload.track
                },
                ...previous
            ].slice(0, 4));
            deepgramUtteranceIdRef.current = '';
        }
    };
    /**
     * Opens the relay socket and, while the captioner still wants automation,
     * retries drops with capped exponential backoff. The relay tears its
     * Deepgram connection down whenever a client socket closes, so every
     * retry re-runs auth -> stream_start against a fresh session; the local
     * audio mixer graph survives across retries untouched.
     */
    const openDeepgramSocket = () => {
        const destination = audioDestinationRef.current;
        if (!autoIntentRef.current || !destination) {
            return;
        }
        const auth = getStoredCaptionRelayAuth();
        if (!auth) {
            failAutoCaptions('Session expired. Sign in again.');
            return;
        }
        closingByUserRef.current = false;
        const attempt = reconnectAttemptRef.current;
        let socket;
        try {
            socket = new WebSocket(getWsUrl());
        }
        catch {
            failAutoCaptions('The caption connection could not be opened.');
            return;
        }
        const roomName = _roomName || window.location.pathname.split('/').filter(Boolean).pop() || 'unknown-room';
        wsRef.current = socket;
        const ownsCurrentSocket = () => wsRef.current === socket && autoIntentRef.current;
        setAutoStatus(attempt > 0
            ? `Live captions dropped — reconnecting (attempt ${attempt} of ${MAX_RECONNECT_ATTEMPTS})...`
            : 'Connecting auto captions...');
        const send = (data) => {
            try {
                socket.send(data);
            }
            catch {
                failAutoCaptions('Audio could not be sent to the caption relay.');
            }
        };
        socket.onopen = () => {
            if (!ownsCurrentSocket()) {
                return;
            }
            send(JSON.stringify({
                data: {
                    ...auth,
                    name: auth.name || participantName
                },
                type: 'auth'
            }));
        };
        socket.onmessage = event => {
            if (!ownsCurrentSocket()) {
                return;
            }
            const message = parseCaptionStreamEvent(event.data);
            if (!message) {
                failAutoCaptions('Live captions received an invalid relay message.');
                return;
            }
            if (message.type === 'auth_success') {
                send(JSON.stringify({
                    data: {
                        language: normalizeCaptionLanguage(languageRef.current),
                        mimeType: DEEPGRAM_MIME_TYPE,
                        provider: 'deepgram',
                        roomName,
                        track: DEFAULT_TRACK
                    },
                    type: 'caption_stream_start'
                }));
            }
            else if (message.type === 'caption_stream_started') {
                // Each relay connection owns one recorder. A repeated start
                // acknowledgment must not leave an extra audio sender running.
                if (mediaRecorderRef.current) {
                    return;
                }
                // A healthy session restarts the backoff ladder.
                reconnectAttemptRef.current = 0;
                const sessionId = message.data.sessionId;
                let recorder;
                try {
                    recorder = new MediaRecorder(destination.stream, { mimeType: DEEPGRAM_MIME_TYPE });
                }
                catch {
                    failAutoCaptions('The audio recorder could not be created.');
                    return;
                }
                mediaRecorderRef.current = recorder;
                deepgramUtteranceIdRef.current = '';
                recorder.onerror = () => {
                    if (ownsCurrentSocket() && mediaRecorderRef.current === recorder) {
                        failAutoCaptions('The audio recorder failed.');
                    }
                };
                recorder.ondataavailable = recorderEvent => {
                    if (!ownsCurrentSocket() || mediaRecorderRef.current !== recorder
                        || !recorderEvent.data.size || socket.readyState !== WebSocket.OPEN) {
                        return;
                    }
                    // Raw WebM/Opus goes straight over the socket as a binary
                    // frame; one stream is bound per connection so chunks need
                    // no base64-in-JSON envelope (~33% wire savings). The relay
                    // routes buffers via ws.captionStreamSession.
                    send(recorderEvent.data);
                };
                try {
                    recorder.start(1000);
                }
                catch {
                    failAutoCaptions('The audio recorder could not start.');
                    return;
                }
                sessionIdRef.current = sessionId;
                setAutoSessionId(sessionId);
                setAutoError(false);
                setAutoStatus('Auto captions running');
            }
            else if (message.type === 'caption_transcript') {
                if (message.data.sessionId === sessionIdRef.current)
                    publishAutoCaption(message.data);
            }
            else if (message.type === 'caption_stream_error' || message.type === 'error' || message.type === 'auth_error') {
                if (message.data.sessionId && message.data.sessionId !== sessionIdRef.current)
                    return;
                // CAPTION_STREAM_NOT_FOUND while we believe a stream is live
                // means the relay lost our binding (an audio chunk racing the
                // provider teardown answers with it). That is a drop, not a
                // rejection — recover through the backoff ladder below.
                if (message.data?.code === 'CAPTION_STREAM_NOT_FOUND'
                    && autoIntentRef.current
                    && !closingByUserRef.current) {
                    stopAutoRecorder();
                    setAutoStatus('Live captions dropped — reconnecting…');
                    try {
                        socket.close();
                    }
                    catch {
                        failAutoCaptions('The caption connection could not recover.');
                    }
                    return;
                }
                // Policy/permission failures are not transient — reconnecting
                // would hammer the same rejection (e.g. the certified-mode
                // eligibility gate). Show the reason; stay stopped. stopAuto-
                // Captions resets status to idle, so the reason comes after.
                failAutoCaptions(message.data.message || 'Auto captions stopped unexpectedly.');
            }
            else if (message.type === 'caption_stream_stopped') {
                const stopData = message.data;
                // A stop for a session this socket no longer runs is a stale
                // ack — ignore it.
                if (stopData.sessionId && sessionIdRef.current
                    && stopData.sessionId !== sessionIdRef.current) {
                    return;
                }
                // Already stopped locally (our own stop's ack, or the teardown
                // following a surfaced caption_stream_error). A late ack must
                // never overwrite the status already on screen with "idle".
                if (!autoIntentRef.current || closingByUserRef.current) {
                    return;
                }
                if (stopData.reason === 'client_stop') {
                    // This client asked for the stop; behave exactly as before.
                    stopAutoCaptions();
                    return;
                }
                // provider_closed, idle_timeout, or any reason the server adds
                // later: the Deepgram leg died, not the intent to caption.
                // Closing the relay socket (without the user flag) routes
                // recovery through onclose's existing backoff ladder, which
                // re-runs auth -> stream_start with the CURRENT language/track.
                stopAutoRecorder();
                setAutoStatus('Live captions dropped — reconnecting…');
                try {
                    socket.close();
                }
                catch {
                    failAutoCaptions('The caption connection could not recover.');
                }
            }
        };
        socket.onerror = () => {
            if (ownsCurrentSocket() && !closingByUserRef.current) {
                setAutoStatus('Auto captions lost their connection.');
            }
        };
        socket.onclose = () => {
            if (!ownsCurrentSocket()) {
                return;
            }
            stopAutoRecorder();
            wsRef.current = null;
            sessionIdRef.current = null;
            deepgramUtteranceIdRef.current = '';
            setAutoSessionId(null);
            // User-initiated stop (or stopAutoCaptions' give-up below) writes
            // its own status; don't reopen on their behalf.
            if (!autoIntentRef.current || closingByUserRef.current) {
                return;
            }
            const nextAttempt = reconnectAttemptRef.current + 1;
            if (nextAttempt > MAX_RECONNECT_ATTEMPTS) {
                // stopAutoCaptions clears the error flag and resets the status
                // to idle, so the persistent failure state is written after it.
                stopAutoCaptions();
                setAutoError(true);
                setAutoStatus('Live captions are down.');
                return;
            }
            reconnectAttemptRef.current = nextAttempt;
            setAutoStatus(`Live captions dropped — reconnecting (attempt ${nextAttempt} of ${MAX_RECONNECT_ATTEMPTS})...`);
            reconnectTimerRef.current = setTimeout(() => {
                reconnectTimerRef.current = null;
                openDeepgramSocket();
            }, Math.min(1000 * 2 ** (nextAttempt - 1), 15000));
        };
    };
    const startAutoCaptions = () => {
        // Toggling off also cancels a pending reconnect backoff mid-ladder.
        if (autoIntentRef.current || wsRef.current || mediaRecorderRef.current) {
            stopAutoCaptions();
            return;
        }
        if (typeof MediaRecorder === 'undefined' || typeof AudioContext === 'undefined'
            || typeof MediaRecorder.isTypeSupported !== 'function'
            || !MediaRecorder.isTypeSupported(DEEPGRAM_MIME_TYPE)) {
            setAutoStatus('This browser cannot send room audio for auto captions.');
            return;
        }
        const audioTracks = getLiveAudioTracks(_tracks);
        if (!audioTracks.length) {
            setAutoStatus('No live room audio tracks are available yet.');
            return;
        }
        try {
            const audioContext = new AudioContext();
            audioContextRef.current = audioContext;
            audioDestinationRef.current = audioContext.createMediaStreamDestination();
            streamRef.current = audioDestinationRef.current.stream;
            const mixer = new CaptionAudioMixer(audioContext, audioDestinationRef.current, () => {
                if (audioMixerRef.current === mixer) {
                    failAutoCaptions('Room audio sources could not be updated.');
                }
            }, count => {
                if (audioMixerRef.current === mixer)
                    setAutoInputCount(count);
            });
            audioMixerRef.current = mixer;
            mixer.sync(audioTracks);
        }
        catch {
            failAutoCaptions('Room audio could not be prepared for captions.');
            return;
        }
        autoIntentRef.current = true;
        reconnectAttemptRef.current = 0;
        setAutoWanted(true);
        openDeepgramSocket();
    };
    if (!_isCaptioner || !_conference) {
        return null;
    }
    return (React.createElement("aside", { style: styles.shell },
        React.createElement("div", { style: styles.card },
            React.createElement("div", { style: styles.header },
                React.createElement("div", null,
                    React.createElement("strong", { style: styles.title }, "Live Captioner")),
                React.createElement("div", { style: styles.headerActions },
                    React.createElement("span", { style: styles.badge }, autoSessionId ? 'Live' : autoWanted ? 'Connecting' : 'Manual'),
                    React.createElement("button", { "aria-expanded": !collapsed, "aria-label": collapsed ? 'Expand captioner panel' : 'Minimize captioner panel', onClick: () => setCollapsed(previous => !previous), style: styles.collapseButton, type: 'button' }, collapsed ? '+' : '–'))),
            collapsed && (React.createElement("div", { style: styles.collapsedStatus }, autoError
                ? 'Live captions are down.'
                : autoWanted
                    ? `Auto captions ${autoSessionId ? 'running' : 'connecting'}`
                    : 'Auto captions off')),
            !collapsed && (React.createElement(React.Fragment, null,
                React.createElement("div", { style: styles.autoPanel },
                    React.createElement("div", null,
                        React.createElement("strong", { style: styles.autoTitle }, "Automated CC"),
                        React.createElement("div", { role: autoError ? 'alert' : 'status', style: styles.autoStatus }, autoWanted && autoSessionId && autoInputCount === 0 ? 'Waiting for room audio…' : autoStatus)),
                    React.createElement("button", { onClick: startAutoCaptions, style: autoWanted
                            ? styles.stopButton
                            : autoError ? styles.errorButton : styles.autoButton, type: 'button' }, autoSessionId
                        ? 'Stop auto captions'
                        : autoWanted ? 'Cancel connection'
                            : autoError ? 'Restart auto captions' : 'Start auto captions')),
                React.createElement("form", { onSubmit: onSubmit, style: styles.form },
                    React.createElement("label", { style: styles.label },
                        "Manual caption track",
                        React.createElement("select", { "aria-label": 'Manual caption track', onChange: event => setTrack(event.target.value), style: styles.select, value: track }, CAPTION_TRACKS.map(option => (React.createElement("option", { key: option.value, value: option.value }, option.label))))),
                    React.createElement("label", { style: styles.label },
                        "Caption language",
                        React.createElement("select", { "aria-label": 'Caption language', disabled: autoWanted, onChange: event => {
                                if (!autoIntentRef.current)
                                    setLanguage(event.target.value);
                            }, style: styles.select, value: language }, languageOptions.map(option => (React.createElement("option", { key: option.value, value: option.value }, option.label))))),
                    React.createElement("label", { style: styles.label },
                        "Caption text",
                        React.createElement("textarea", { "aria-label": 'Caption text', dir: 'auto', onKeyDown: event => {
                                if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                                    event.preventDefault();
                                    publishCaption();
                                }
                            }, onChange: event => {
                                setCaptionText(event.target.value);
                                setPublishError(null);
                            }, placeholder: 'Caption text', rows: 4, style: styles.textarea, value: captionText })),
                    publishError && (React.createElement("div", { role: 'alert', style: styles.publishError }, publishError)),
                    editingEntry && (React.createElement("div", { style: styles.editorNotice },
                        "Replacing your line from",
                        ' ',
                        new Date(editingEntry.timestamp).toLocaleTimeString([], {
                            hour: 'numeric',
                            minute: '2-digit'
                        }),
                        ".")),
                    React.createElement("div", { style: styles.footer },
                        React.createElement("div", { style: styles.publishRow },
                            editingEntry && (React.createElement("button", { onClick: () => {
                                    setEditingEntry(null);
                                    setPublishError(null);
                                }, style: styles.secondaryButton, type: 'button' }, "New line")),
                            React.createElement("button", { disabled: !captionText.trim(), style: captionText.trim() ? styles.button : { ...styles.button, ...styles.buttonDisabled }, type: 'submit' }, editingEntry ? 'Replace Caption' : 'Publish Caption')))),
                sentCaptions.length > 0 && (React.createElement("div", { style: styles.preview },
                    React.createElement("strong", { style: styles.previewTitle }, "Recent captions"),
                    sentCaptions.map(entry => {
                        const isEditing = editingEntry?.id === entry.id;
                        return (React.createElement("div", { key: entry.id, style: isEditing ? styles.previewRowActive : styles.previewRow },
                            React.createElement("span", { style: styles.previewTime },
                                new Date(entry.timestamp).toLocaleTimeString([], {
                                    hour: 'numeric',
                                    minute: '2-digit'
                                }),
                                " \u00B7 ",
                                entry.language,
                                " \u00B7 ",
                                entry.track,
                                isEditing ? ' · being replaced' : ''),
                            React.createElement("span", { dir: 'auto', style: styles.previewText }, entry.text),
                            !isEditing && (React.createElement("button", { onClick: () => {
                                    setPublishError(null);
                                    setEditingEntry(entry);
                                    setCaptionText(entry.text);
                                }, style: styles.recallButton, type: 'button' }, "Recall & replace"))));
                    }))))))));
}
function mapStateToProps(state) {
    const localParticipant = getLocalParticipant(state);
    return {
        _conference: state['features/base/conference'].conference,
        _displayName: getDisplayName(state),
        _isCaptioner: isCaptioner(),
        _localParticipantId: localParticipant?.id,
        _localParticipantName: localParticipant?.name,
        _requestingSubtitles: state['features/subtitles']._requestingSubtitles,
        _roomName: getRoomName(state),
        _tracks: state['features/base/tracks']
    };
}
const styles = {
    publishError: {
        background: '#3b1820',
        border: '1px solid #e79aa8',
        borderRadius: 10,
        color: '#ffe3e9',
        fontSize: 14,
        lineHeight: 1.5,
        padding: 12
    },
    autoButton: {
        background: '#3ea86a',
        border: 'none',
        borderRadius: 10,
        color: '#06130b',
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: 800,
        padding: '9px 12px'
    },
    autoPanel: {
        alignItems: 'center',
        background: 'rgba(255, 255, 255, 0.06)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: 12,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
        justifyContent: 'space-between',
        padding: 12
    },
    autoStatus: {
        color: 'rgba(248, 243, 234, 0.68)',
        fontSize: 12,
        lineHeight: 1.35,
        marginTop: 2
    },
    autoTitle: {
        color: '#f8f3ea',
        display: 'block',
        fontSize: 13
    },
    badge: {
        background: 'rgba(242, 185, 75, 0.18)',
        borderRadius: 999,
        color: '#f2c56d',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.08em',
        padding: '6px 10px',
        textTransform: 'uppercase'
    },
    button: {
        background: '#f2b94b',
        border: 'none',
        borderRadius: 10,
        color: '#2b1b0f',
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: 700,
        padding: '10px 14px'
    },
    buttonDisabled: {
        cursor: 'not-allowed',
        opacity: 0.48
    },
    card: {
        backdropFilter: 'blur(16px)',
        background: 'rgba(22, 16, 12, 0.78)',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        borderRadius: 16,
        boxShadow: '0 10px 35px rgba(0, 0, 0, 0.28)',
        boxSizing: 'border-box',
        color: '#f8f3ea',
        display: 'grid',
        gap: 12,
        maxHeight: 'calc(100dvh - 116px)',
        minWidth: 0,
        overflowY: 'auto',
        padding: 16,
        width: '100%'
    },
    collapseButton: {
        background: 'rgba(255, 255, 255, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.14)',
        borderRadius: 10,
        color: '#f8f3ea',
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: 800,
        lineHeight: 1,
        minWidth: 34,
        padding: '6px 9px'
    },
    collapsedStatus: {
        color: 'rgba(248, 243, 234, 0.7)',
        fontSize: 12,
        lineHeight: 1.4
    },
    errorButton: {
        background: '#7d2416',
        border: '1px solid #ff8d73',
        borderRadius: 10,
        color: '#ffd9d0',
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: 800,
        padding: '9px 12px'
    },
    editorNotice: {
        background: 'rgba(242, 185, 75, 0.12)',
        border: '1px solid rgba(242, 185, 75, 0.35)',
        borderRadius: 10,
        color: '#f2c56d',
        fontSize: 12,
        lineHeight: 1.4,
        padding: '8px 10px'
    },
    footer: {
        alignItems: 'center',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
        justifyContent: 'space-between'
    },
    form: {
        display: 'grid',
        gap: 12,
        minWidth: 0
    },
    header: {
        alignItems: 'flex-start',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
        justifyContent: 'space-between'
    },
    headerActions: {
        alignItems: 'center',
        display: 'flex',
        gap: 8
    },
    helper: {
        color: 'rgba(248, 243, 234, 0.7)',
        flex: '1 1 180px',
        fontSize: 12,
        lineHeight: 1.4
    },
    label: {
        color: '#f8f3ea',
        display: 'grid',
        fontSize: 12,
        fontWeight: 600,
        gap: 6,
        minWidth: 0
    },
    preview: {
        borderTop: '1px solid rgba(255, 255, 255, 0.08)',
        display: 'grid',
        gap: 8,
        paddingTop: 12
    },
    previewRow: {
        display: 'grid',
        gap: 2
    },
    previewRowActive: {
        border: '1px solid rgba(242, 185, 75, 0.45)',
        borderRadius: 10,
        display: 'grid',
        gap: 2,
        padding: 8
    },
    previewText: {
        color: '#f8f3ea',
        fontSize: 14,
        lineHeight: 1.5,
        overflowWrap: 'anywhere',
        textAlign: 'start',
        whiteSpace: 'pre-wrap'
    },
    previewTime: {
        color: 'rgba(248, 243, 234, 0.52)',
        fontSize: 11,
        textTransform: 'uppercase'
    },
    previewTitle: {
        color: '#f2c56d',
        fontSize: 12,
        letterSpacing: '0.08em',
        textTransform: 'uppercase'
    },
    publishRow: {
        alignItems: 'center',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8
    },
    recallButton: {
        alignSelf: 'end',
        background: 'transparent',
        border: '1px solid rgba(242, 185, 75, 0.55)',
        borderRadius: 8,
        color: '#f2c56d',
        cursor: 'pointer',
        fontSize: 11,
        fontWeight: 700,
        justifySelf: 'start',
        padding: '5px 10px'
    },
    secondaryButton: {
        background: 'rgba(255, 255, 255, 0.08)',
        border: 'none',
        borderRadius: 10,
        color: '#f8f3ea',
        cursor: 'pointer',
        fontSize: 13,
        fontWeight: 700,
        padding: '10px 12px'
    },
    select: {
        background: 'rgba(255, 255, 255, 0.06)',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        borderRadius: 10,
        color: '#f8f3ea',
        fontSize: 14,
        padding: '10px 12px'
    },
    shell: {
        bottom: 96,
        left: 20,
        position: 'absolute',
        width: 'min(360px, calc(100% - 40px))',
        zIndex: 31
    },
    subtitle: {
        color: 'rgba(248, 243, 234, 0.74)',
        fontSize: 13,
        lineHeight: 1.45,
        marginTop: 4
    },
    textarea: {
        boxSizing: 'border-box',
        background: 'rgba(255, 255, 255, 0.06)',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        borderRadius: 12,
        color: '#f8f3ea',
        fontFamily: 'inherit',
        fontSize: 16,
        lineHeight: 1.5,
        minWidth: 0,
        overflowWrap: 'anywhere',
        padding: '12px',
        resize: 'vertical',
        textAlign: 'start',
        width: '100%'
    },
    stopButton: {
        background: '#ff7055',
        border: 'none',
        borderRadius: 10,
        color: '#210500',
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: 800,
        padding: '9px 12px'
    },
    title: {
        display: 'block',
        fontSize: 15
    }
};
export default connect(mapStateToProps)(CaptionComposer);
